"use client";

import React from "react";
import { useField } from "formik";
import { AlertCircle, ChevronDown } from "lucide-react";

interface Option {
  label: string;
  value: string | number | boolean;
}

interface SelectFieldProps {
  label: string;
  name: string;
  options: Option[];
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  required?: boolean;
  labelClassName?: string;
  selectClassName?: string;
  variant?: "light" | "dark";
}

export const SelectField: React.FC<SelectFieldProps> = ({
  label,
  name,
  options,
  placeholder = "Select an option",
  disabled = false,
  className = "",
  required = false,
  labelClassName = "",
  selectClassName = "",
  variant = "light",
}) => {
  const [field, meta] = useField(name);
  const isError = meta.touched && meta.error;

  const isDark = variant === "dark";

  const defaultLabelStyles = isDark
    ? "text-[10px] text-text-3 font-bold tracking-widest uppercase flex items-center gap-2"
    : "text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold";

  const defaultSelectStyles = isError
    ? `border-red-500/50 ${isDark ? "bg-red-500/10" : "bg-red-50/10"} focus:border-red-500 focus:ring-4 focus:ring-red-500/10`
    : isDark
      ? "bg-black-3 border-border-1 text-text-1 focus:border-gold-dim placeholder:text-text-3"
      : "bg-slate-50 border-slate-200 text-slate-800 focus:border-gold-core/50 focus:bg-white focus:ring-4 focus:ring-gold-core/10 group-hover:border-slate-300";

  return (
    <div className={`space-y-1.5 ${className}`}>
      <div className="flex items-center justify-between">
        <label htmlFor={name} className={labelClassName || defaultLabelStyles}>
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      </div>
      <div className="relative group">
        <select
          {...field}
          id={name}
          disabled={disabled}
          className={`w-full px-4 py-2.5 border text-xs outline-none transition-all rounded-lg appearance-none font-medium tracking-wide disabled:opacity-50 disabled:cursor-not-allowed
            ${selectClassName || defaultSelectStyles}`}
        >
          <option value="" disabled className="text-slate-400">
            {placeholder}
          </option>
          {options.map((option) => (
            <option key={String(option.value)} value={String(option.value)}>
              {option.label}
            </option>
          ))}
        </select>
        <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400 group-hover:text-slate-500 transition-colors">
          <ChevronDown size={14} />
        </div>
      </div>
      {isError && (
        <div className="flex items-center gap-1 text-[10px] text-red-500 font-bold animate-in fade-in slide-in-from-right-1">
          <AlertCircle size={10} />
          <span>{meta.error}</span>
        </div>
      )}
    </div>
  );
};
